<template>
  <div>
    <loading :active.sync="loading"></loading>
    <div class="jumbotron">
      <ol class="breadcrumb">
        <li class="breadcrumb-item"><a href="#/home">Home</a></li>
        <li class="breadcrumb-item"><a href="#/sync">Sync</a></li>
        <li class="breadcrumb-item active">Merge</li>
      </ol>
    </div>
    <div class="row">
      <div class="col">
        <feedback v-if="showFeedback"
          v-bind:message="feedbackMessage"
          v-bind:spin="showSpinner"
          v-bind:success="success"></feedback>
        <form>
          <div v-if="!syncConfig">
            <div class="form-group">
              <div class="input-group">
                <label class="col-3 col-form-label"><i class="fas fa-database"></i> Data Source</label>
                <model-select :options="dataSourceOptions" v-model="selectedDataSourceOption" placeholder="Select Helium datasouce"></model-select>
              </div>
            </div>
            <div class="form-group">
              <div class="input-group">
                <label class="col-3 col-form-label"><i class="fas fa-mobile-alt"></i> App</label>
                <model-select :options="dataSourceApps" v-model="dataSourceApp" placeholder="Select Helium app"></model-select>
              </div>
            </div>
          </div>
          <div v-if="syncConfig">
            <button type="button" class="btn btn-xs btn-info pull-right" @click="toggleExpand">
              <div v-if="!expand">Show Advanced</div>
              <div v-else>Hide Advanced</div>
            </button>
            <div class="row">
              <div class="col">
                <fieldset class="form-group">
                  <legend>HeliumJourney Sync Middleware:</legend>
                  <div class="alert alert-info pull-right" role="alert">
                    <i class="fa fa-info-circle" aria-hidden="true"></i>
                    <strong>Please note</strong><br>
                    <p>Remember to disable the middleware before attempting a Helium Upgrade. When set as enabled data for this integration will be processed.</p>
                  </div>
                  <label class="col-form-label">Middleware Sync Status</label>
                  <div class="form-check">
                    <label class="form-check-label">
                      <input
                        id="enabledRadio1"
                        name="enabledRadios"
                        type="radio"
                        v-model="enabledRadio"
                        class="form-check-input"
                        value="Enabled">
                      Enabled
                    </label>
                  </div>
                  <div class="form-check">
                    <label class="form-check-label">
                        <input
                          id="enabledRadio2"
                          name="enabledRadios"
                          type="radio"
                          v-model="enabledRadio"
                          class="form-check-input"
                          value="Disabled">
                        Disabled
                    </label>
                  </div>
                </fieldset>
              </div>
              <div class="col" v-if="expand">
                <fieldset class="form-group">
                  <legend>Journey To Helium:</legend>
                  <div class="form-check">
                    <label class="form-check-label">
                      <input
                        id="enabledRadio3"
                        name="enableJourneyToHeliumRadios"
                        type="radio"
                        v-model="enableHeliumToJourneyRadio"
                        class="form-check-input"
                        value="Enabled">
                      Enabled
                    </label>
                  </div>
                  <div class="form-check">
                    <label class="form-check-label">
                        <input
                          id="enabledRadio4"
                          name="enableJourneyToHeliumRadios"
                          type="radio"
                          v-model="enableHeliumToJourneyRadio"
                          class="form-check-input"
                          value="Disabled">
                      Disabled
                    </label>
                  </div>
                  <small class="form-text text-muted">When set as enabled data updated on Journey will be synced to Helium's backend database.</small>
                </fieldset>
              </div>
              <div class="col" v-if="expand">
                <fieldset class="form-group">
                  <legend>Helium To Journey:</legend>
                  <div class="form-check">
                    <label class="form-check-label">
                      <input
                        id="enabledRadio5"
                        name="enabledHeliumToJourneyRadios"
                        type="radio"
                        v-model="enableJourneyToHeliumRadio"
                        class="form-check-input"
                        value="Enabled">
                      Enabled
                    </label>
                  </div>
                  <div class="form-check">
                    <label class="form-check-label">
                        <input
                          id="enabledRadio6"
                          name="enabledHeliumToJourneyRadios"
                          type="radio"
                          v-model="enableJourneyToHeliumRadio"
                          class="form-check-input"
                          value="Disabled">
                        Disabled
                    </label>
                  </div>
                  <small class="form-text text-muted">When set as enabled data updated on Helium will be synced to Journey's backend database.</small>
                </fieldset>
              </div>
            </div>
            <div class="row">
              <div class="col-6" ref="heliumBookmarkIdHelpRef">
                <fieldset>
                  <legend>Helium:</legend>
                  <div class="form-group">
                    <label class="col-form-label">Base URL</label>
                    <input v-model="syncConfig.helium.baseUrl"
                      class="form-control"
                      type="text"
                      readonly>
                      <small class="form-text text-muted">Base URL for Helium.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Schema Name</label>
                    <input v-model="syncConfig.helium.dbSchema"
                      class="form-control"
                      type="text"
                      readonly>
                      <small class="form-text text-muted">Schema name of Helium app.</small>
                  </div>

                  <div class="form-group">
                    <label class="col-form-label">Changelog Starting Version</label>
                    <input v-model="syncConfig.heliumToJourney.changeLogMinVersionAsString"
                      v-bind:readonly="readonlyVersions"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">This integration was setup at this version, sync changes after this Helium bookmark ID.<button type="button" class="btn-sm help-btn-padding" :class="{ 'btn-outline-info': showHeliumBookmarkIdHelp, 'btn-info': !showHeliumBookmarkIdHelp }" @click="btnHeliumBookmarkIdHelp">More info</button></small>
                    <small class="form-text text-muted" v-if="showHeliumBookmarkIdHelp">
                      Each database transaction persisted on Helium is assigned a unique and incrementing Bookmark ID. If a record is created, deleted or changes state after this Bookmark ID the data will be merged to Journey.

                      See <a href="https://wiki.mezzanineware.com/display/HTUT/Change+Tracking">Helium Change tracking Documentation</a> for more info.
                    </small>
                    <small class="form-text text-muted" v-if="showHeliumBookmarkIdHelp">
                      A changelog version with value 0 indicates all changes from the beginning of time on the app should be processed.
                    </small>
                    <div class="card card-block card-outline-secondary" v-if="showHeliumBookmarkIdHelp">
                      <div class="card-title">
                        <div class="row">
                          <div class="col">
                            <small class="form-text text-muted" >
                              Use the following SQL statements to extract the last bookmark and create a new bookmark if needed.
                            </small>
                          </div>
                          <div class="form-inline float-right">
                            <button
                              type="button"
                              class="btn btn-primary"
                              data-toggle="tooltip"
                              title="Copy Command to Clipboard"
                              v-clipboard:copy="buildNewHeliumVersionSql"
                              v-clipboard:success="onCopyCallBack">
                              <i class="fa fa-clipboard"></i>
                            </button>
                          </div>
                        </div>
                      </div> <!-- card-title -->
                      <text-editor
                        v-if="showHeliumBookmarkIdHelp"
                        v-bind:contentProp="buildNewHeliumVersionSql"
                        v-bind:width="textEditorWidthHelium"
                        v-bind:height="textEditorHeightHelium"
                        v-bind:readonly="textEditorReadOnly"
                        v-bind:scrollToLine="textEditorScrollToLine"
                        v-bind:lang="textEditorLangHelium">
                      </text-editor>
                    </div> <!-- card -->
                  </div>
                  <div class="form-group" v-if="readonlyVersions">
                    <label class="col-form-label">Changelog Current Version</label>
                    <input v-model="heliumSyncStatus"
                      class="form-control"
                      type="text"
                      readonly="">
                    <small class="form-text text-muted">
                      Integration is currently at this bookmark version since {{new Date(new Number(heliumLastUpdatedMillis))}}
                    </small>
                  </div>
                  <div class="form-group" v-if="expand">
                    <label class="col-form-label">Change Log Skip Tables</label>
                    <input v-model="changeLogSkipTablesTmp"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Skip specific tables from syncing to Journey (Optional)<button type="button" class="btn-sm help-btn-padding" :class="{ 'btn-outline-info': showHeliumSkipHelp, 'btn-info': !showHeliumSkipHelp }" @click="btnHeliumSkipHelp">More info</button></small>
                      <small class="form-text text-muted" v-if="showHeliumSkipHelp">Values are comma seperated (without spaces). Helium persistent objects which are not marked as @NotTracked must be explicitly added here.</small>
                      <small class="form-text text-muted" v-if="showHeliumSkipHelp"></small>

                      <div class="card card-block card-outline-secondary" v-if="showHeliumSkipHelp">
                        <div class="card-title">
                          <div class="row">
                            <div class="col">
                              <small class="form-text text-muted">
                                Use the following SQL statement to list all SQL views and SQL materialized views.
                                Please note: If there aren't any materialized views it will leave a trailing comma that should not be added to the "Change Log Skip Tables" input.
                              </small>
                            </div>
                            <div class="form-inline float-right">
                              <button
                                type="button"
                                class="btn btn-primary"
                                data-toggle="tooltip"
                                title="Copy Command to Clipboard"
                                v-clipboard:copy="buildSkipHeliumViewsSql"
                                v-clipboard:success="onCopyCallBack">
                                <i class="fa fa-clipboard"></i>
                              </button>
                            </div>
                        </div> <!-- card-title -->
                        <text-editor
                          v-if="showHeliumSkipHelp"
                          v-bind:contentProp="buildSkipHeliumViewsSql"
                          v-bind:width="textEditorWidthHelium"
                          v-bind:height="textEditorHeightHelium"
                          v-bind:readonly="textEditorReadOnly"
                          v-bind:scrollToLine="textEditorScrollToLine"
                          v-bind:lang="textEditorLangHelium">
                        </text-editor>
                      </div> <!-- card -->
                    </div>

                  </div>
                </fieldset>
              </div>
              <div class="col-6">
                <fieldset>
                  <legend>Journey:</legend>
                  <div class="form-group">
                    <label class="col-form-label">Base URL</label>
                    <input v-model="syncConfig.journey.baseUrl"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Base URL for Journey. To find this, go to the API Management page for your app on Journey. The baseURL is the domain before /api/v4/{app-id}. 
                      eg. https://{app-deployment-id}.backend.{region-code}.journeyapps.com</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Journey API Username</label>
                    <input v-model="syncConfig.journey.restAPICredentials.userName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Journey backend API basic auth username.</small>
                    <small class="form-text text-muted">Integration leverages Journey's Backend API. See <a href="https://docs.journeyapps.com/v4/reference#backend-api">Journey API Documentation</a> for more info.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Journey API Password</label>
                    <input v-model="syncConfig.journey.restAPICredentials.password" class="form-control" type="password" autocomplete="password">
                      <small class="form-text text-muted">Journey backend API basic auth password.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Oplog Starting Version</label>
                    <input v-model="syncConfig.journeyToHelium.oplogMinVersionAsString"
                      v-bind:readonly="readonlyVersions"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Sync changes after this Journey Version.<button type="button" class="btn-sm help-btn-padding" @click="btnJourneyVersionHelp" :class="{ 'btn-outline-info': showJourneyVersionHelp, 'btn-info': !showJourneyVersionHelp }">More info</button></small>
                    <small class="form-text text-muted" v-if="showJourneyVersionHelp">
                      Each individual database operation on Journey is assigned a unique and incrementing version number known as the Sequence ID on Journey. If a record is created, deleted or changes state after this Sequence ID the data will be merged to Helium. See <a href="https://docs.journeyapps.com/v4/reference#oplog">Journey Oplog Documentation</a> for more info.
                    </small>
                    <small class="form-text text-muted" v-if="showJourneyVersionHelp">A value of 0 indicates all changes from the beginning of time on the app should be processed.</small>

                    <div class="card card-block card-outline-secondary" v-if="showJourneyVersionHelp">
                      <div class="card-title">
                        <div class="row">
                          <div class="col">
                            <small class="form-text text-muted" >
                              Use the following in terminal to generate a new Journey version that represents now.
                            </small>
                          </div>
                          <div class="form-inline float-right">
                            <button
                              type="button"
                              class="btn btn-primary"
                              data-toggle="tooltip"
                              title="Copy Command to Clipboard"
                              v-clipboard:copy="buildNewJourneyVersionSql"
                              v-clipboard:success="onCopyResetJourneyVersion">
                              <i class="fa fa-clipboard"></i>
                            </button>
                          </div>
                        </div>
                      </div> <!-- card-title -->
                      <text-editor
                        v-if="showJourneyVersionHelp"
                        v-bind:contentProp="buildNewJourneyVersionSql"
                        v-bind:width="textEditorWidthJourney"
                        v-bind:height="textEditorHeightJourney"
                        v-bind:readonly="textEditorReadOnly"
                        v-bind:scrollToLine="textEditorScrollToLine"
                        v-bind:lang="textEditorLangJourney">
                      </text-editor>
                    </div> <!-- card -->
                  </div>
                  <div class="form-group" v-if="readonlyVersions">
                    <label class="col-form-label">Oplog Current Version</label>
                    <input v-model="journeySyncStatus"
                      class="form-control"
                      type="text"
                      readonly>
                    <small class="form-text text-muted">
                      Integration is currently at this bookmark version since {{new Date(new Number(journeyLastUpdatedMillis))}}
                    </small>
                  </div>
                  <div class="form-group" v-if="expand">
                    <label class="col-form-label">Oplog Skip Object Types</label>
                    <input v-model="oplogSkipObjectTypesTmp"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Skip specific tables from syncing to Helium (Optional)<button type="button" class="btn-sm help-btn-padding" :class="{ 'btn-outline-info': showJourneySkipHelp, 'btn-info': !showJourneySkipHelp }" @click="btnJourneySkipHelp">More info</button></small>
                      <small class="form-text text-muted" v-if="showJourneySkipHelp">Values are comma seperated (without spaces). Journey objects which are not declared on Helium must be explicitly added here. Using <a href="https://docs.journeyapps.com/docs/app-database#section-localdb">LocalDB</a> is suggested over using this field.</small>
                  </div>
                </fieldset>
              </div>
            </div>
            <fieldset class="form-group" v-if="expand">
              <legend>Journey enrollments and metadata:</legend>
              <div class="row">
                <div class="col-6">
                  <div class="form-group">
                    <div class="form-check">
                      <label class="form-check-label">
                        <input
                          id="enrollmentRadio1"
                          name="enrollmentRadios"
                          type="radio"
                          v-model="enabledEnrollmentRadio"
                          class="form-check-input"
                          value="Enabled">
                        Enabled
                      </label>
                      <div class="form-check">
                        <label class="form-check-label">
                          <input
                            id="enrollmentRadio2"
                            name="enrollmentRadios"
                            type="radio"
                            v-model="enabledEnrollmentRadio"
                            class="form-check-input"
                            value="Disabled">
                          Disabled
                        </label>
                      </div>
                      <small class="form-text text-muted">When set as enabled mobile user re-enrollment tasks are processed and Journey user enrollment meta data is merged into the application data model.</small>
                    </div>
                  </div>
                  <div class="form-group" v-if="syncConfig.journeyToHeliumEnrollment">
                    <div class="form-check">
                      <label class="form-check-label">
                        <input
                          id="enrollmentModeRadio1"
                          name="enrollmentModeRadios"
                          type="radio"
                          v-model="enrollmentModeRadio"
                          class="form-check-input"
                          value="Single device per user">
                        Single device per user
                      </label>
                      <div class="form-check">
                        <label class="form-check-label">
                          <input
                            id="enrollmentModeRadio2"
                            name="enrollmentModeRadios"
                            type="radio"
                            v-model="enrollmentModeRadio"
                            class="form-check-input"
                            value="Multiple devices per user">
                          Multiple devices per user
                        </label>
                      </div>
                    </div>
                    <small class="form-text text-muted">Journey handles user enrollments in two possible ways. If support for multiple devices per user is enabled for a Journey app then 'Multiple devices per user' should be selected otherwise 'Single device per user' should be used.</small>
                  </div>
                  <div class="form-group" v-if="syncConfig.journeyToHeliumEnrollment">
                    <div v-if="enrollmentMode === 'user'">
                      <small class="form-text text-muted">Journey user enrollment metadata is extraced and persisted into the mobile user worker objects '{{syncConfig.journeyToHeliumEnrollment.heliumObjectName}}' and '{{syncConfig.journeyToHeliumEnrollment.journeyObjectName}}'. </small>
                      <small class="form-text text-muted">Journey docs: <a href="https://docs.journeyapps.com/v4/reference#managing-mobile-users">Mobile users</a></small>
                    </div>
                    <div v-else>
                      <small class="form-text text-muted">Journey user enrollment metadata is extraced and persisted into the mobile user worker objects '{{syncConfig.journeyToHeliumEnrollment.heliumObjectName}}' and '{{syncConfig.journeyToHeliumEnrollment.journeyObjectName}}'. User session data is persisted into '{{syncConfig.journeyToHeliumEnrollment.sessionObjectName}}' and user session container data is persisted into '{{syncConfig.journeyToHeliumEnrollment.sessionContainerObjectName}}'.</small>
                      <small class="form-text text-muted">Journey docs: <a href="https://docs.journeyapps.com/v4/reference#managing-app-users-and-sessions">Mobile users and sessions</a></small>
                    </div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group" v-if="syncConfig.journeyToHeliumEnrollment">
                    <label class="col-form-label">Update Interval Mins</label>
                    <select class="form-control" v-model="syncConfig.journeyToHeliumEnrollment.updateIntervalMins">
                      <option v-for="i in updateIntervalValues">
                      {{ i }}
                      </option>
                    </select>
                    <small class="form-text text-muted">Number of minutes to wait between runs of the Journey-to-Helium synchronization of enrollment data. The default value is 15 and the minimum value is 5.</small>
                  </div>
                </div>
              </div>

              <legend v-if="syncConfig.journeyToHeliumEnrollment">
                User:
              </legend>
              <small class="form-text text-muted" v-if="syncConfig.journeyToHeliumEnrollment">The User object represents  users who are enrolled on a Journey app. Each user has available metadata for enrollment and session management.</small>

              <div class="row" v-if="syncConfig.journeyToHeliumEnrollment">
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Helium Object Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.heliumObjectName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the Helium persistent object that represents the Journey user.</small>
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Journey Object Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.journeyObjectName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the Journey object that represents the Journey user.</small>
                  </div>
                </div>
              </div>

              <legend v-if="syncConfig.journeyToHeliumEnrollment">
                User field names:
              </legend>
              <small class="form-text text-muted" v-if="syncConfig.journeyToHeliumEnrollment">Names of fields in Helium persistent object for Journey user.</small>

              <div class="row" v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'user'">
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Allowed Access Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.allowedAccessFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the flag that indicates if the user is allowed to access the Journey app. The field type may be boolean or string. For string fields the possible values are Yes and No.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Enrolled Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.enrolledFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the flag that indicates if the user is currently enrolled. The field type may be boolean or string. For string fields the possible values are Yes and No.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Journey Launcher Version Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.journeyLauncherVersionFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the version for the Journey Launcher last used by the user. The field type must be string.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Device OS Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.deviceOsFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the description of the device operating system last used by the user. The field type must be string.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Perform Re-Enrollment Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.performReEnrollmentFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the flag that indicates if re-enrollment must be triggered for the user. When specified, this field is read by the Helium-to-Journey sync process. Setting the flag to a value that equates to "true", will trigger a re-enrollment against the Journey user management API. Once the re-enrollment has been triggered, the flag will be set back to a value that equates to "false" and the reEnrollmentLastProcessedFieldName will be updated. Note that Helium-to-Journey synchronization must be enabled for this flag to be processed. The field type may be boolean or string. For string fields the possible values are yes, no, true, false, t and f.  For string fields the values are not case sensitive.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Generate Enrollment Code Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.generateEnrollmentCodeFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains a flag to trigger generating enrollment code. The field type may be boolean or string. For string fields the possible values are yes, no, true, false, t and f. For string fields the values are not case sensitive.</small>
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Device Model Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.deviceModelFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the description of the device model last used by the user. The field type must be string.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Last Connected Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.lastConnectedFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the date and time when the user was last connected. The field type may be string, date or dateTime. For string fields the value will be an ISO 8601 date and time string in coordinated universal time (UTC).</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Barcode Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.barcodeFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the URL to the Journey enrollment barcode for the user. The field type must be string.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">URL Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.urlFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the URL to the Journey enrollment page for the user. The field type must be string.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Re Enrollment Last Processed</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.reEnrollmentLastProcessedFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the date and time when the last re-enrollment request was processed for the user. The field type may be string, date or dateTime. For string fields the value will be an ISO 8601 date and time string in coordinated universal time (UTC).</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Enrollment Code Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.enrollmentCodeFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains an enrollment code. An enrollment code is used when enrolling with the journey-enrollment-service. The field type must be string</small>
                  </div>
                </div>
              </div>

              <div class="row" v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Generate Enrollment Code Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.generateEnrollmentCodeFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains a flag to trigger generating enrollment code. The field type may be boolean or string. For string fields the possible values are yes, no, true, false, t and f. For string fields the values are not case sensitive.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Enrollment Code Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.enrollmentCodeFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains an enrollment code. An enrollment code is used when enrolling with the journey-enrollment-service. The field type must be string.</small>
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Locked Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.lockedFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the flag that indicates if a user is locked out of the Journey application. When a user is locked they are signed out if busy in a session and cannot log into the Journey application. The field type may be boolean or string. For string fields the possible values are Yes and No.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Last Connected At Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.lastConnectedAtFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the date and time when the user was last connected. The field type may be string, date or dateTime. For string fields the value will be an ISO 8601 date and time string in coordinated universal time (UTC).</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Enrolled Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.enrolledFieldName"
                      class="form-control"
                      type="text">
                      <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the flag that indicates if the user is currently enrolled. The field type may be boolean or string. For string fields the possible values are Yes and No.</small>
                  </div>
                </div>
              </div>

              <legend v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">
                User Session:
              </legend>
              <small class="form-text text-muted" v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">The User session object represents the sessions of users who are enrolled on a Journey app.</small>

              <div class="row" v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Session Object Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the Helium persistent object that contains device session data for Journey users. This table will be truncated and populated each update interval.</small>
                  </div>
                </div>
                <div class="col-6">
                </div>
              </div>

              <legend v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">
                User session object field names:
              </legend>
              <small class="form-text text-muted"  v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">
              Names of fields in Helium persistent object for Journey user session data</small>

              <div class="row" v-if="syncConfig.journeyToHeliumEnrollment && syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames && enrollmentMode === 'session'">
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">State Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames.stateFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains string representation of a Sessions state. The field type must be string.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Last Authenticated At Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames.lastAuthenticatedAtFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the date and time when the user was last authenticated. The field type may be string, date or dateTime. For string fields the value will be an ISO 8601 date and time string in coordinated universal time (UTC).</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Created At Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames.createdAtFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the date and time when the user was created. The field type may be string, date or dateTime. For string fields the value will be an ISO 8601 date and time string in coordinated universal time (UTC).</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Helium Object Fk Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames.heliumObjectFkFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name of the id field in the Helium persistent object that contains the FK to the mobile user object on Helium '{{syncConfig.journeyToHeliumEnrollment.heliumObjectName}}'. The field type must be string.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Perform Re-Authenticate Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames.performReAuthenticateFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains a flag to trigger re authenticating a users session. The field type may be boolean or string. For string fields the possible values are yes, no, true, false, t and f. For string fields the values are not case sensitive.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Re-Authenticated Last Processed Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames.reAuthenticateLastProcessedFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the date and time when the last re-authenticate request was processed for the user. The field type may be string, date or dateTime. For string fields the value will be an ISO 8601 date and time string in coordinated universal time (UTC).</small>
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Signed Out At Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames.signedOutAtFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the date and time when the user was last signed out. The field type may be string, date or dateTime. For string fields the value will be an ISO 8601 date and time string in coordinated universal time (UTC).</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Last Connected At Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames.lastConnectedAtFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains the date and time when the user was last connected. The field type may be string, date or dateTime. For string fields the value will be an ISO 8601 date and time string in coordinated universal time (UTC).</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Label Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionObjectFieldNames.labelFieldname"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">Name for the field in the Helium persistent object that contains Journey Session Label. The field type must be string.</small>
                  </div>
                </div>
              </div>

              <legend v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">
                User Session Container:
              </legend>
              <small class="form-text text-muted"  v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">
              The User session container object represents the container of the session of enrolled Journey app users</small>

              <div class="row" v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Session Container Object Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionContainerObjectName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted" v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">Name for the Helium persistent object that contains device container data for Journey user sessions</small>
                  </div>
                </div>
                <div class="col-6">
                </div>
              </div>

              <legend v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">User Session Container field names:</legend>
              <small class="form-text text-muted" v-if="syncConfig.journeyToHeliumEnrollment && enrollmentMode === 'session'">
              Names of fields in Helium persistent object for Journey user session container data</small>

              <div class="row" v-if="syncConfig.journeyToHeliumEnrollment && syncConfig.journeyToHeliumEnrollment.sessionContainerObjectFieldNames && enrollmentMode === 'session'">
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Version Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionContainerObjectFieldNames.versionFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">
                    Name for the field in the Helium persistent object that contains Journey container version for a device. An example value is '3.30.3'. The field type must be string.</small>
                  </div>
                   <div class="form-group">
                    <label class="col-form-label">Bundle ID Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionContainerObjectFieldNames.bundleIdFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">
                    Name for the field in the Helium persistent object that contains Journey application bundle id. An example value is xyz.appinstall.myapp. The field type must be string.</small>
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label class="col-form-label">Platform Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionContainerObjectFieldNames.platformFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">
                    Name for the field in the Helium persistent object that contains device platform name. An example value is IOS or Chrome. The field type must be string.</small>
                  </div>
                  <div class="form-group">
                    <label class="col-form-label">Session Object FK Field Name</label>
                    <input v-model="syncConfig.journeyToHeliumEnrollment.sessionContainerObjectFieldNames.sessionObjectFkFieldName"
                      class="form-control"
                      type="text">
                    <small class="form-text text-muted">
                    Name for the field in the Helium persistent object that contains FK the to the session that this container belongs. The field type must be UUID</small>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
          <fieldset>
            <div class="btn-group pull-right" role="group">
              <button type="button" class="btn btn-primary" @click="saveBtn">Save</button>
              <button type="button" class="btn btn-secondary" @click="backBtn">Back</button>
            </div>
          </fieldset>
        </form>
        <!-- <div class="row" v-if="loading">
          <div class="col d-flex justify-content-center">
            <i class="fa fa-spinner fa-pulse fa-3x fa-fw"></i>
          </div>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
  import Feedback from '@/components/Feedback.vue'
  import { alert } from 'vue-strap'
  import TextEditor from '@/components/TextEditor.vue'
  import { ModelSelect } from 'vue-search-select'
  import Loading from 'vue-loading-overlay'
  import 'vue-loading-overlay/dist/vue-loading.min.css'
  import 'vue-search-select/dist/VueSearchSelect.css'
  export default {
    components: {
      Feedback,
      alert,
      TextEditor,
      ModelSelect,
      Loading
    },
    data () {
      return {
        username: 'a',
        password: 'a',
        list: [],
        loading: false,
        success: false,
        feedbackMessage: null,
        showFeedback: null,
        syncConfig: null,
        expand: false,
        enabledRadio: null,
        enableHeliumToJourneyRadio: null,
        enableJourneyToHeliumRadio: null,
        showSpinner: false,
        enabledEnrollmentRadio: null,
        enrollmentModeRadio: null,
        enrollmentMode: 'user',
        journeyToHeliumEnrollmentTmp: null,
        changeLogSkipTablesTmp: null,
        oplogSkipObjectTypes: null,
        dataSources: [],
        dataSource: null,
        dataSourceApps: [],
        dataSourceApp: null,
        showHeliumBookmarkIdHelp: false,
        showJourneyVersionHelp: false,
        showHeliumSkipHelp: false,
        textEditorWidthHelium: 0,
        textEditorWidthJourney: 0,
        textEditorHeightHelium: 0,
        textEditorHeightJourney: 0,
        textEditorReadOnly: true,
        textEditorScrollToLine: 0,
        textEditorLangHelium: 'sql',
        textEditorLangJourney: 'powershell',
        alertDuration: 5000,
        alertMessage: null,
        showJourneySkipHelp: null,
        readonlyVersions: false,
        feedbackLogsMessage: null,
        showLogsSpinner: true,
        logsSuccess: false,
        textBlockWidth: 500,
        textBlockHeight: 600,
        textBlockReadOnly: true,
        textBlockLang: 'sql',
        dataSourceOptions: [],
        selectedDataSourceOption: {
          value: '',
          text: ''
        }
      }
    },
    methods: {
      pingDatabase: function (dataSourceParam) {
        let vm = this
        let endpoint = vm.$parent.baseUrl + '/portal-backend/api/schema-access/ping-database'
        var config = {}
        config['environment'] = vm.$parent.environment.name
        config['dataSource'] = dataSourceParam
        vm.axios.post(endpoint, config)
          .then(function (response) {
            // console.log(response.data)
            var element = {}
            element['heliumDataSourceName'] = dataSourceParam.heliumDataSourceName
            element['response'] = response.data.response
            element['dataSource'] = dataSourceParam
            vm.dataSources.push(element)
            var dataSourceOption = { value: dataSourceParam, text: element.heliumDataSourceName }
            vm.dataSourceOptions.push(dataSourceOption)
          })
          .catch(function (error) {
            console.log(error.response)
          })
      },
      getDatabaseHostApps: function () {
        let vm = this
        let endpoint = vm.$parent.baseUrl + '/portal-backend/api/schema-access/app-schemas'
        let config = {}
        config['environment'] = vm.$parent.environment.name
        config['dataSource'] = vm.dataSource
        vm.loading = true
        vm.feedbackMessage = null
        vm.showFeedback = false
        vm.axios.post(endpoint, config)
          .then(function (response) {
            console.log(response.data)
            var existingIds = vm.$parent.syncApps.map(function (key) {
              return key.id
            })
            response.data.filter(function (key) {
              return existingIds.indexOf(key.id) <= -1
            }).forEach(function (element) {
              let text = element.name + ' (' + element.id + ')'
              vm.dataSourceApps.push({ value: element.id, text: text })
            })
            vm.loading = false
            vm.success = true
            if (vm.dataSourceApps.length <= 0) {
              vm.feedbackMessage = 'No apps found on ' + endpoint
              vm.showFeedback = true
            }
          })
          .catch(function (error) {
            vm.loading = false
            vm.success = false
            vm.showFeedback = true
            vm.feedbackMessage = error
          })
      },
      getSyncUrl: function () {
        let vm = this
        let endpoint = vm.$parent.baseUrl + '/sync'
        if (vm.$parent.environment.name === 'Zambia-production') {
          endpoint += '-zm'
        } else if (vm.$parent.environment.name !== 'Production') {
          endpoint += '-stb'
        }
        endpoint += '/api/'
        return endpoint
      },
      getAxiosConfig: function () {
        let vm = this
       	let syncEnv = vm.$parent.environment.name
        return {
          auth: {
            username: vm.$parent.username,
            password: vm.$parent.password
          },
	  headers: {
            "X-Helium-Environment": syncEnv
	  }
        }
      },
      toggleExpand: function () {
        let vm = this
        vm.expand = vm.expand !== true
      },
      mergeDone: function (data) {
        let vm = this
        console.log(data)
        vm.loading = false
        if (vm.syncConfig.enabled) {
          vm.$router.push({name: 'Sync', params: {appid: vm.syncConfig.helium.appId}})
        } else {
          vm.backBtn()
        }
      },
      mergeWithWebSocket: function () {
        let vm = this
        let wsUrl = vm.$parent.httpUrlToWebSockeUrl(vm.getSyncUrl() + 'ws')
        vm.$parent.socket = new WebSocket(wsUrl)
        vm.$parent.socket.onopen = function () {
          vm.$parent.socket.onmessage = function (response) {
            console.log(response.data)
            vm.$parent.socket.close()
            vm.mergeDone(response.data)
          }
          vm.$parent.socket.onclose = function (response) {
            // console.log('onclose')
            // console.log(response)
          }
          let websocketRequest = {}
          websocketRequest['appId'] = vm.syncConfig.helium.appId
          websocketRequest['type'] = 'MERGE'
          websocketRequest['config'] = vm.syncConfig
          vm.$parent.socket.send(JSON.stringify(websocketRequest))
        }
      },
      saveBtn: function () {
        let vm = this
        vm.loading = true
        vm.syncConfig.journey.appId = vm.syncConfig.journey.restAPICredentials.userName
        vm.syncConfig.heliumToJourney.changeLogMinVersion = vm.syncConfig.heliumToJourney.changeLogMinVersionAsString
        vm.syncConfig.journeyToHelium.oplogMinVersion = vm.syncConfig.journeyToHelium.oplogMinVersionAsString

        vm.syncConfig.heliumToJourney.changeLogSkipTables = vm.changeLogSkipTablesTmp ? vm.changeLogSkipTablesTmp.split(',') : null
        vm.syncConfig.journeyToHelium.oplogSkipObjectTypes = vm.oplogSkipObjectTypesTmp ? vm.oplogSkipObjectTypesTmp.split(',') : null
        vm.mergeWithWebSocket()
      },
      backBtn: function () {
        let vm = this
        vm.$router.go(-1)
      },
      setRadios: function () {
        let vm = this
        if (vm.syncConfig.enabled) {
          vm.enabledRadio = 'Enabled'
        } else {
          vm.enabledRadio = 'Disabled'
        }
        if (vm.syncConfig.enableJourneyToHelium) {
          vm.enableHeliumToJourneyRadio = 'Enabled'
        } else {
          vm.enableHeliumToJourneyRadio = 'Disabled'
        }
        if (vm.syncConfig.enableHeliumToJourney) {
          vm.enableJourneyToHeliumRadio = 'Enabled'
        } else {
          vm.enableJourneyToHeliumRadio = 'Disabled'
        }
      },
      setSyncConfigTemplate: function () {
        let vm = this
        let endpoint = vm.getSyncUrl() + 'get-template'
        var config = {}
        config['environment'] = vm.$parent.environment.name
        config['uuid'] = vm.dataSourceApp
        config['dataSource'] = vm.dataSource
        config['journeyUserMode'] = vm.enrollmentMode
        var syncExists = vm.$parent.syncApps.some(function (key) {
          return key.id === vm.dataSourceApp
        })
        if (syncExists) {
          vm.feedbackMessage = 'There is already an integration for ' + vm.dataSourceApp
          vm.success = false
          vm.showFeedback = true
          return
        }
        vm.loading = true
        vm.axios.post(endpoint, config, {
          auth: {
            username: vm.$parent.username,
            password: vm.$parent.password
          }
        })
          .then(function (response) {
            vm.syncConfig = response.data
            vm.journeyToHeliumEnrollmentTmp = vm.syncConfig.journeyToHeliumEnrollment
            vm.changeLogSkipTablesTmp = null
            vm.oplogSkipObjectTypesTmp = null
            vm.setRadios()
            vm.enabledEnrollmentRadio = 'Enabled'
            vm.setEnrollmentModeRadio()
            vm.loading = false
            vm.success = true
          })
          .catch(function (error) {
            // console.log(error)
            vm.feedbackMessage = 'Unable to fetch template: ' + error.response.data
            vm.success = false
            vm.showFeedback = true
            vm.loading = false
          })
      },
      setSyncConfig: function (appId) {
        let vm = this
        let endpoint = vm.getSyncUrl() + 'get/' + appId
        var config = vm.getAxiosConfig()
        vm.loading = true
	vm.axios.get(endpoint, config)
          .then(function (response) {
            vm.syncConfig = response.data
            vm.setRadios()
            vm.journeyToHeliumEnrollmentTmp = vm.syncConfig.journeyToHeliumEnrollment
            vm.changeLogSkipTablesTmp = vm.syncConfig.heliumToJourney.changeLogSkipTables.join(',')
            vm.oplogSkipObjectTypesTmp = vm.syncConfig.journeyToHelium.oplogSkipObjectTypes.join(',')
            if (vm.syncConfig.journeyToHeliumEnrollment) {
              vm.enabledEnrollmentRadio = 'Enabled'
              vm.enrollmentMode = vm.syncConfig.journeyToHeliumEnrollment.type
              vm.setEnrollmentModeRadio()
            } else {
              vm.enabledEnrollmentRadio = 'Disabled'
              vm.setEnrollmentModeRadio()
            }
            vm.loading = false
            vm.success = true
          })
          .catch(function (error) {
            vm.feedbackMessage = (error.response && error.response.status === 502) ? 'Bad Gateway - Invalid response from the upstream server' : error.response.data
            vm.success = false
            vm.loading = false
          })
      },
      setEnrollmentTemplate: function () {
        let vm = this
        let endpoint = vm.getSyncUrl() + 'get-template-enrollment'
        var config = {}
        config['environment'] = vm.$parent.environment.name
        config['uuid'] = vm.dataSourceApp
        config['journeyUserMode'] = vm.enrollmentMode
        vm.loading = true
        vm.axios.post(endpoint, config, {
          auth: {
            username: vm.$parent.username,
            password: vm.$parent.password
          }
        })
          .then(function (response) {
            vm.syncConfig.journeyToHeliumEnrollment = response.data
            vm.loading = false
            vm.success = true
          })
          .catch(function (error) {
            // console.log(error)
            vm.success = false
            vm.feedbackMessage = 'Unable to fetch enrollment template ' + error.response.data
            vm.showFeedback = true
            vm.loading = false
          })
      },
      btnHeliumBookmarkIdHelp: function () {
        let vm = this
        if (vm.showHeliumBookmarkIdHelp) {
          vm.showHeliumBookmarkIdHelp = false
        } else {
          vm.showHeliumBookmarkIdHelp = true
        }
      },
      btnJourneyVersionHelp: function () {
        let vm = this
        if (vm.showJourneyVersionHelp) {
          vm.showJourneyVersionHelp = false
        } else {
          vm.showJourneyVersionHelp = true
        }
      },
      btnHeliumSkipHelp: function () {
        let vm = this
        if (vm.showHeliumSkipHelp) {
          vm.showHeliumSkipHelp = false
        } else {
          vm.showHeliumSkipHelp = true
        }
      },
      btnJourneySkipHelp: function () {
        let vm = this
        if (vm.showJourneySkipHelp) {
          vm.showJourneySkipHelp = false
        } else {
          vm.showJourneySkipHelp = true
        }
      },
      setEnrollmentModeRadio: function () {
        let vm = this
        if (vm.syncConfig && vm.syncConfig.journeyToHeliumEnrollment) {
          if (vm.syncConfig.journeyToHeliumEnrollment.type === 'user') {
            vm.enrollmentModeRadio = 'Single device per user'
          } else {
            vm.enrollmentModeRadio = 'Multiple devices per user'
          }
        } else {
          vm.enrollmentModeRadio = 'Single device per user'
        }
      },
      getHeliumBookmarkInfoTextEditorHeight: function () {
        return Math.round(window.innerHeight * 60 / 100)
      },
      resizeEditor: function () {
        let vm = this
        let widthValue = Math.round(window.innerWidth * 42 / 100)
        vm.textEditorWidthHelium = widthValue
        vm.textEditorWidthJourney = widthValue
        vm.textEditorHeightHelium = vm.getHeliumBookmarkInfoTextEditorHeight()
        vm.textEditorHeightJourney = 35
        vm.textEditorHeightWsLogs = 200
      },
      onCopyCallBack: function () {
        let vm = this
        vm.$Simplert.open({ title: 'Copied', message: 'Paste me into postgres!', type: 'success' })
      },
      onCopyResetJourneyVersion: function () {
        let vm = this
        vm.$Simplert.open({ title: 'Copied', message: 'Paste me into terminal!', type: 'success' })
      },
      findApp: function () {
        let vm = this
        return vm.$parent.syncApps.find(function (key) {
          return key.id === vm.syncConfig.helium.appId
        })
      }
    },
    created () {
      let vm = this
      vm.$parent.environment.dataSources.forEach(vm.pingDatabase)
      vm.resizeEditor()
      if (vm.$route.params.appid) {
        vm.setSyncConfig(vm.$route.params.appid)
        vm.readonlyVersions = true
      }
    },
    beforeDestroy: function () {
      window.removeEventListener('resize', this.resizeEditor)
    },
    mounted: function () {
      window.addEventListener('resize', this.resizeEditor)
    },
    watch: {
      'selectedDataSourceOption': function (newValue) {
        this.dataSource = newValue.value
        this.getDatabaseHostApps()
      },
      'dataSourceApp': function (newValue) {
        this.setSyncConfigTemplate()
      },
      'enabledRadio': function (newValue) {
        this.syncConfig.enabled = this.syncConfig !== null && newValue === 'Enabled'
      },
      'enableHeliumToJourneyRadio': function (newValue) {
        let vm = this
        vm.syncConfig.enableJourneyToHelium = vm.syncConfig !== null && newValue === 'Enabled'
      },
      'enableJourneyToHeliumRadio': function (newValue) {
        let vm = this
        vm.syncConfig.enableHeliumToJourney = vm.syncConfig !== null && newValue === 'Enabled'
      },
      'enabledEnrollmentRadio': function (newValue) {
        let vm = this
        if (newValue !== 'Enabled') {
          vm.journeyToHeliumEnrollmentTmp = vm.syncConfig.journeyToHeliumEnrollment
          vm.syncConfig.journeyToHeliumEnrollment = null
        } else {
          if (vm.journeyToHeliumEnrollmentTmp) {
            vm.syncConfig.journeyToHeliumEnrollment = vm.journeyToHeliumEnrollmentTmp
          } else {
            vm.setEnrollmentTemplate()
          }
        }
      },
      'enrollmentModeRadio': function (newValue) {
        let vm = this
        if (newValue === 'Single device per user') {
          vm.enrollmentMode = 'user'
        } else {
          vm.enrollmentMode = 'session'
        }
        if (vm.syncConfig.journeyToHeliumEnrollment != null && vm.syncConfig.journeyToHeliumEnrollment.type !== vm.enrollmentMode) {
          vm.setEnrollmentTemplate()
        }
      }
    },
    computed: {
      updateIntervalValues: function () {
        let values = []
        for (var i = 5; i <= 60; i = i + 5) {
          values.push(i)
        }
        return values
      },
      buildNewHeliumVersionSql: function () {
        let vm = this
        if (!vm.syncConfig.helium.dbSchema) {
          return 'Unable to build SQL: No schema name!'
        }
        return 'set search_path TO ' + vm.syncConfig.helium.dbSchema + ';\n\n' +
        '--Creating new Helium Bookmark:\n' +
        '--Changes from previous bookmarks will be skipped\n' +
          'insert into __he_sync_bookmark__(tx_id, tx_tstamp, entity, client_id)\n' +
          'with src as (\n' +
          '\tselect\n' +
          '\t\ttxid_current() "tx_id",\n' +
          '\t\tmax(tx_id) "last_export_version_id",\n' +
          '\t\t(extract(\'epoch\' from now()) * 1000)::bigint "tstamp"\n' +
          '\tfrom __he_sync_obj_change__\n' +
          ') select\n' +
          '\ttx_id,\n' +
          '\tnow(),\n' +
          '\tjsonb_build_object(\n' +
          '\t\t\'id\', tx_id,\n' +
          '\t\t\'millis\',tstamp,\n' +
          '\t\t\'clientId\',\'d4ec6977-28ab-4caf-8872-edd39b471436\',\n' +
          '\t\t\'skippedVersionIds\',\'[]\'::jsonb,\n' +
          '\t\t\'lastExportVersionId\',last_export_version_id\n' +
          '\t),\n' +
          '\t\'d4ec6977-28ab-4caf-8872-edd39b471436\'::uuid\n' +
          'from src;\n\n' +
          '--Lookup last bookmark:\n' +
          'select tx_id from __he_sync_bookmark__ order by tx_tstamp desc limit 1;'
      },
      buildSkipHeliumViewsSql: function () {
        return 'WITH q AS (\n' +
          '\tSELECT v.viewname,\n' +
          '\tm.matviewname\n' +
          '\tFROM pg_views AS v\n' +
          '\tLEFT JOIN pg_matviews AS m ON v.schemaname = m.schemaname\n' +
          '\tWHERE v.schemaname NOT IN (\'pg_catalog\', \'information_schema\')\n' +
          '\tAND v.schemaname = \'' + this.syncConfig.helium.dbSchema + '\' ORDER BY v.viewname)\n' +
          'SELECT CONCAT(array_to_string(array_agg(distinct "viewname"),\',\'), \',\', array_to_string(array_agg(distinct "matviewname"),\',\')) FROM q;'
      },
      buildNewJourneyVersionSql: function () {
        return 'python -c "import time;print int(time.time())<<32;"'
      },
      heliumSyncStatus: function () {
        let vm = this
        if (!vm.$parent.syncApps) {
          return null
        }
        var result = vm.findApp()
        return (result) ? result.heliumSyncStatus : null
      },
      heliumLastUpdatedMillis: function () {
        let vm = this
        if (!vm.$parent.syncApps) {
          return null
        }
        var result = vm.findApp()
        return (result) ? result.heliumLastUpdated : null
      },
      journeySyncStatus: function () {
        let vm = this
        if (!vm.$parent.syncApps) {
          return null
        }
        var result = vm.findApp()
        return (result) ? result.journeySyncStatus : null
      },
      journeyLastUpdatedMillis: function () {
        let vm = this
        if (!vm.$parent.syncApps) {
          return null
        }
        var result = vm.findApp()
        return (result) ? result.journeyLastUpdated : null
      }
    }
  }
</script>

<style scoped>
  .card-block-watermark {
    font-size: 2rem;
    position: absolute;
    bottom: 5px;
    right: 5px;
    opacity: 0.3;
  }

  .help-btn-padding {
    margin-left: 10px;
  }
</style>



// WEBPACK FOOTER //
// SyncForm.vue?521a4178